import React from 'react';
import { map, split, toLower, isEmpty } from 'lodash';
import { ReactComponent as RestrictedIcon } from 'new/components/icons/fi-rr-warning.svg';
import { ReactComponent as ConfidentialIcon } from 'new/components/icons/fi-rr-lock.svg';
import { ReactComponent as InternalIcon } from 'new/components/icons/fi-rr-building.svg';
import { ReactComponent as PublicIcon } from 'new/components/icons/fi-rr-globe.svg';
import { ReactComponent as GenAiIcon } from 'new/components/icons/genai.svg';
import { sortObjectEntries } from 'new/utils/sortObjectEntries';
import Tooltip from 'js/components/elements/mui/Tooltip';
// Define preferred order of keys
const SEMANTIC_INFO_PREFERRED_KEYS = [
  'sensitivity',
  'classification_rationale',
  'confidence',
  'semantic_type',
  'semantic_category',
];

const sensitivityMap = {
  SecretRestricted: {
    label: 'Secret/Restricted',
    type: 'sensitivity-SecretRestricted',
    icon: <RestrictedIcon />,
  },
  Confidential: {
    label: 'Confidential',
    type: 'sensitivity-Confidential',
    icon: <ConfidentialIcon />,
  },
  Internal: {
    label: 'Internal',
    type: 'sensitivity-Internal',
    icon: <InternalIcon />,
  },
  Public: {
    label: 'Public',
    type: 'sensitivity-Public',
    icon: <PublicIcon />,
  },
};

// Format the property name for display
const formatPropertyName = (key) => {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatValue = (value) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    if (value.length === 1) {
      return String(value[0]);
    }

    return (
      <div className={'bullet-list'}>
        {value.map((item, index) => (
          <div key={index} className={'bullet-point'}>
            <span>•</span>
            <span>{String(item)}</span>
          </div>
        ))}
      </div>
    );
  }

  return String(value);
};

class RecipeBuilderAttributeTypes extends React.PureComponent {
  render() {
    const { attr, limit, semanticInfo } = this.props;

    if (!attr) {
      return null;
    }

    const { __type, __formatDisplay, __name } = attr;
    let typeParts = split(__type, ' or ');
    let formatParts = split(__formatDisplay, ' or ');

    if (isEmpty(typeParts)) {
      typeParts = [__type];
    }

    if (isEmpty(formatParts)) {
      formatParts = [__formatDisplay];
    }

    const numTypes = typeParts.length;

    const sortedSemanticInfo = semanticInfo
      ? sortObjectEntries(Object.entries(semanticInfo), SEMANTIC_INFO_PREFERRED_KEYS)
      : [];

    const sensitivity = (semanticInfo?.sensitivity ?? '').toString().replace('/', '');

    const sensitivityInfo = sensitivityMap[sensitivity] || {
      label: sensitivity,
      type: `sensitivity-Public`,
      icon: <GenAiIcon />,
    };

    return (
      <ul className="recipe-builder-attribute-types">
        {semanticInfo?.sensitivity && sortedSemanticInfo.length > 0 && (
          <Tooltip
            tooltipTheme="fieldInfo"
            placement="bottom-start"
            title={
              <div className={'semantic-schema-tooltip-content'}>
                {sortedSemanticInfo.map(([key, value], index) => {
                  if (!value) return null;
                  return (
                    <div key={key} className={'tooltip-item'}>
                      <span>
                        <b>{formatPropertyName(key)}:</b> {formatValue(value)}
                      </span>
                    </div>
                  );
                })}
                <div className={'tooltip-footer'}>
                  <GenAiIcon height={12} width={12} />
                  <span className={'tooltip-footer-text'}>NOVA Generated Semantic Schema</span>
                </div>
              </div>
            }
          >
            <li className={`attr-type attr-type-semantic ${sensitivityInfo.type}`}>
              {sensitivityInfo.icon}
              {semanticInfo.semantic_type}
            </li>
          </Tooltip>
        )}
        {map(typeParts.slice(0, limit || numTypes), (type, index) => (
          <li key={index} className={`attr-type attr-type-${toLower(type)}`}>
            {formatParts[index] || type}
          </li>
        ))}
        {limit && limit < numTypes && <li className="attr-type">+{numTypes - limit}</li>}
      </ul>
    );
  }
}

export default RecipeBuilderAttributeTypes;
